import {isNotNil} from 'ramda';

import {GetSignableDocumentListResponseBody} from '@dms/api/core';
import {PhoneNumber} from '@dms/api/shared';

import {Nullish} from 'shared/web';

import {BASE_PATH_SEPARATOR} from '../constants/BASE_PATH_SEPARATOR';
import {DefaultSignatoriesFormData} from '../types/DefaultSignatoriesFormData';
import {DocumentDefaultSignatoriesFormData} from '../types/DocumentDefaultSignatoriesFormData';

type SeededSignatoriesFormValues = Record<
  string,
  | {
      id?: string;
      name?: string;
      email?: string;
      phoneNumber?: PhoneNumber;
    }
  | undefined
>;

/**
 * Maps default signatory values onto every document/signatory form field
 * (`<fileId>_<signatoryId>` keys). A document's own defaults win over the shared ones.
 * Fields whose signatory role has no default stay undefined so required-field validation reports them.
 */
export const buildSignatoriesFormValues = (
  documents: GetSignableDocumentListResponseBody[] | Nullish,
  defaults: DefaultSignatoriesFormData,
  documentDefaults: DocumentDefaultSignatoriesFormData = {}
) =>
  (documents ?? []).reduce<SeededSignatoriesFormValues>((formValues, document) => {
    const documentValues = document.metadata.reduce((values, signatory) => {
      const signatoryDefaults =
        documentDefaults[document.sourceFileId]?.[signatory.signatoryPerson] ??
        defaults[signatory.signatoryPerson];

      return {
        ...values,
        [`${document.sourceFileId}${BASE_PATH_SEPARATOR}${signatory.id}`]: isNotNil(
          signatoryDefaults
        )
          ? {
              id: signatoryDefaults.id,
              name: signatoryDefaults.name ?? undefined,
              email: signatoryDefaults.email ?? undefined,
              phoneNumber: signatoryDefaults.phoneNumber,
            }
          : undefined,
      };
    }, {});

    return {...formValues, ...documentValues};
  }, {});
